// What clinic agents couldn't do, for the "Agent gaps" panel on the SuperAdmin page
// (docs/superpowers/specs/2026-10-15-agent-gaps-design.md). backend/api/agents/views.py serves
// GET /api/agents/gaps/ (view=groups or view=entries) and POST /api/agents/gaps/triage/, to the
// staff the SuperAdmin page admits. Every call sends the signed-in user's token, with one
// refresh-and-retry after a 401 (staffRequest); a refusal throws the server's text, which
// gapErrorText turns into words for the panel.

import { getBackendURL } from "../components/env";
import { staffRequest } from "./staffRequest";

const PATH = "/api/agents/gaps/";
// staffRequest's text when the server answered no error of its own.
const UNREACHABLE = "The agent gaps couldn't be reached. Try again.";

// The gaps URL with `params`. A list value repeats its parameter (category=a&category=b); empty
// values are left out.
function gapsUrl(params) {
  const url = new URL(`${getBackendURL()}${PATH}`);
  Object.entries(params).forEach(([key, value]) => {
    (Array.isArray(value) ? value : [value]).forEach((v) => {
      if (v !== undefined && v !== null && v !== "") url.searchParams.append(key, String(v));
    });
  });
  return url.toString();
}

// { groups, totals, truncated, read_at } for `filters`: category, reason, area, action, role,
// schema, agent_type, source, status, since, until (YYYY-MM-DD, UTC, inclusive). read_at is the
// server's time when it read the rows.
export function fetchGapGroups(filters = {}) {
  return staffRequest(gapsUrl({ ...filters, view: "groups" }), { failure: UNREACHABLE });
}

// { entries, page, has_more }: one group's entries under the same filters, 100 a page, newest first.
export function fetchGapEntries(groupKey, filters = {}, page = 1) {
  return staffRequest(
    gapsUrl({ ...filters, view: "entries", group_key: groupKey, page }),
    { failure: UNREACHABLE },
  );
}

// { updated }. `body` is { ids, status, note } or { group_key, filters, as_of, status, note }:
// as_of limits a whole group to the gaps recorded by then, the ones the person saw.
export function triageGaps(body) {
  return staffRequest(`${getBackendURL()}${PATH}triage/`, { method: "POST", body, failure: UNREACHABLE });
}

// What the panel shows for a failed call, in the manner of the agents UI's agentErrors.js. The
// server answers a sentence, shown as it comes, or, from its shared checks, a code, which is never
// shown as it is. A network failure (fetch's TypeError) or an error with nothing to say reads as
// the gaps couldn't be reached.
const CODES = new Map([
  ["forbidden", "Your account can't see the agent gaps."],
  ["view_as_session_is_read_only", "Agent gaps aren't available in a view-as session."],
  ["invalid JSON", "Something went wrong. Try again."],
]);

export function gapErrorText(err) {
  if (err instanceof TypeError) return UNREACHABLE;
  const message = typeof err === "string" ? err : err?.message;
  if (typeof message !== "string" || message.trim() === "") return UNREACHABLE;
  return CODES.get(message) ?? message;
}
